import { useEffect, useCallback } from "react";
import { useSidebar } from "./SidebarProvider";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export function SidebarOverlay() {
  const { expanded, mobileOpen, closeMobile, toggle } = useSidebar();
  const isMd = useMediaQuery("(min-width: 768px)");
  const isLg = useMediaQuery("(min-width: 1024px)");

  // Overlay is visible when:
  // - At md (768-1023): expanded is true (drawer over icon rail)
  // - Below md (<768): mobileOpen is true (full drawer)
  // - At lg+: never (sidebar is always visible)
  const visible = !isLg && (isMd ? expanded : mobileOpen);

  const dismiss = useCallback(() => {
    if (isMd) {
      toggle();
    } else {
      closeMobile();
    }
  }, [isMd, toggle, closeMobile]);

  // Body scroll lock
  useEffect(() => {
    if (visible) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      };
    }
  }, [visible]);

  // Escape key dismisses
  useEffect(() => {
    if (!visible) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        dismiss();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, dismiss]);

  // Close mobile drawer on resize past lg
  useEffect(() => {
    if (isLg && mobileOpen) {
      closeMobile();
    }
  }, [isLg, mobileOpen, closeMobile]);

  // Close expanded state on resize past lg
  useEffect(() => {
    if (isLg && expanded) {
      toggle();
    }
  }, [isLg, expanded, toggle]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-ink/40 sidebar-transition"
      style={{ top: "var(--banner-h)" }}
      onClick={dismiss}
      aria-hidden="true"
    />
  );
}
